import { useMemo } from 'react';
import ChapterSection from '../ChapterSection/ChapterSection';
import ChapterMap from '../ChapterMap/ChapterMap';
import type { Point, ReinfoLayerSummary, ReinfoPortfolio } from '@/lib/api.types';
import type { GeometryLayer, LegendItem, MapSpec } from '@/lib/map-spec';
import type { LeadSpec, RowSpec } from '@/lib/report-content';
import { TILES } from '@/lib/tiles';
import {
  classifyFireproof,
  classifyZoning,
  FIREPROOF_GROUP_COLOR,
  PLANNING_ICON_FOR,
  ZONING_GROUP_COLOR,
  type FireproofGroup,
  type ZoningGroup,
} from '@/lib/chapters';
import {
  ChapterLead,
  CheckList,
  EmptyState,
  RawDetails,
  RowList,
  rankToStatus,
  rankToStatusLabel,
} from './shared';

type Props = {
  portfolio: ReinfoPortfolio;
  site: Point;
};

const SUBMAP_HEIGHT = 220;

const ZONING_GROUP_LABEL: Record<ZoningGroup, string> = {
  residential: '住居系',
  commercial: '商業系',
  industrial: '工業系',
  other: 'その他',
};

const FIREPROOF_GROUP_LABEL: Record<FireproofGroup, string> = {
  strict: '防火地域',
  quasi: '準防火地域',
  other: 'その他',
};

function buildZoningMap(layer: ReinfoLayerSummary, site: Point): MapSpec {
  const geos: GeometryLayer[] = [];
  const seenGroups = new Set<ZoningGroup>();
  const polys = layer.feature_geometries ?? [];
  const labels = layer.feature_labels ?? [];
  polys.forEach((g, i) => {
    const polyLabel = labels[i] ?? layer.value_label;
    const group = classifyZoning(polyLabel);
    const color = ZONING_GROUP_COLOR[group];
    geos.push({
      data: g,
      style: { stroke: false, fillColor: color, fillOpacity: 0.35 },
      label: polyLabel || layer.name,
    });
    seenGroups.add(group);
  });
  const legend: LegendItem[] = [];
  for (const group of seenGroups) {
    legend.push({ color: ZONING_GROUP_COLOR[group], label: `用途地域: ${ZONING_GROUP_LABEL[group]}` });
  }
  return {
    key: 'zoning',
    point: site,
    tileUrl: TILES.pale,
    zoom: 16,
    height: SUBMAP_HEIGHT,
    geometries: geos,
    markers: [],
    legend,
    caption: '用途地域（住居・商業・工業など）',
    emptyMessage: 'この付近では用途地域の区域データが見つかりませんでした。',
  };
}

function buildFireproofMap(layer: ReinfoLayerSummary, site: Point): MapSpec {
  const geos: GeometryLayer[] = [];
  const seenGroups = new Set<FireproofGroup>();
  const polys = layer.feature_geometries ?? [];
  const labels = layer.feature_labels ?? [];
  polys.forEach((g, i) => {
    const polyLabel = labels[i] ?? layer.value_label;
    const group = classifyFireproof(polyLabel);
    const color = FIREPROOF_GROUP_COLOR[group];
    geos.push({
      data: g,
      style: {
        stroke: false,
        fillColor: color,
        fillOpacity: 0.35,
      },
      label: polyLabel || layer.name,
    });
    seenGroups.add(group);
  });
  const legend: LegendItem[] = [];
  for (const group of seenGroups) {
    legend.push({
      color: FIREPROOF_GROUP_COLOR[group],
      label: FIREPROOF_GROUP_LABEL[group],
    });
  }
  return {
    key: 'fireproof',
    point: site,
    tileUrl: TILES.pale,
    zoom: 16,
    height: SUBMAP_HEIGHT,
    geometries: geos,
    markers: [],
    legend,
    caption: '防火・準防火地域',
    emptyMessage: 'この付近では防火・準防火地域の指定区域は見つかりませんでした。',
  };
}

export type BuildingContent = {
  lead: LeadSpec;
  maps: MapSpec[];
  rows: RowSpec[];
  checkItems: string[];
  emptyText: string | null;
};

// 建築章の中身。画面 (ChapterBuilding) と PDF 冊子が共有する純関数。
export function buildBuildingContent(portfolio: ReinfoPortfolio, site: Point): BuildingContent {
  const zoningLayer = portfolio.layers.find((l) => l.endpoint === 'XKT002');
  const fireproofLayer = portfolio.layers.find((l) => l.endpoint === 'XKT014');

  const maps: MapSpec[] = [];
  if (zoningLayer) maps.push(buildZoningMap(zoningLayer, site));
  if (fireproofLayer) maps.push(buildFireproofMap(fireproofLayer, site));

  const rows: RowSpec[] = portfolio.layers.map((layer) => {
    const isFireproof = layer.endpoint === 'XKT014';
    const detail = isFireproof
      ? (layer.matched_count > 0 ? (layer.value_label ?? '該当あり') : '該当なし')
      : (layer.value_label ?? `該当 ${layer.matched_count} 件`);
    return {
      icon: PLANNING_ICON_FOR[layer.endpoint] ?? 'building-community',
      label: layer.name,
      detail,
      status: rankToStatus(layer.rank),
      statusLabel: rankToStatusLabel(layer.rank, layer.matched_count > 0 ? '該当あり' : '該当なし'),
    };
  });

  return {
    lead: { label: portfolio.label, message: portfolio.buyer_message },
    maps,
    rows,
    checkItems: portfolio.check_items,
    emptyText:
      portfolio.layers.length === 0 ? '用途地域・防火地域などの都市計画情報を取得できませんでした。' : null,
  };
}

export default function ChapterBuilding({ portfolio, site }: Props) {
  const content = useMemo(() => buildBuildingContent(portfolio, site), [portfolio, site]);

  return (
    <ChapterSection id="building">
      <ChapterLead {...content.lead} />

      {content.maps.map((spec) => (
        <ChapterMap key={spec.key} spec={spec} />
      ))}

      {content.emptyText ? (
        <EmptyState>{content.emptyText}</EmptyState>
      ) : (
        <RowList rows={content.rows} />
      )}

      <CheckList items={content.checkItems} />
      <RawDetails portfolio={portfolio} />
    </ChapterSection>
  );
}
